import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  Palette,
  Printer,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './InvoicePreview.module.css';

import InvoiceActionPanel from './components/InvoiceActionPanel/InvoiceActionPanel';
import InvoiceDocument from './components/InvoiceDocument/InvoiceDocument';
import InvoiceTemplatePanel from './components/InvoiceTemplatePanel/InvoiceTemplatePanel';

import { useStore } from '../../context/StoreContext';
import useAccess from '../../hooks/useAccess';
import {
  API_ORIGIN,
  buildInvoicePdf,
  buildShareMessage,
  cleanCustomerName,
  downloadBlob,
  fmtDate as formatDate,
  getInvoiceBrand,
  getPaperWidthPx,
  normalizePhone,
  printInvoiceNode,
  sanitizeFileName,
  shareInvoice,
  toAppInvoiceLink,
} from './components/Invoiceexport';

// =============================================================================
// Invoice preview: template, print, PDF, share with the client, complete a draft.
// Access (module "billing", same rules as the server):
//   PDF               billing.download_bill
//   Print             billing.print_bill
//   Complete a draft  billing.complete_bill or billing.create
//   WhatsApp (chat)   billing.send_whatsapp
//   WhatsApp API      billing.send_whatsapp_api
//   SMS               billing.send_sms
//   Email             billing.send_email
// =============================================================================

const API_BASE = `${API_ORIGIN}/api/v1/smiify/sm/invoices`;
const SETTINGS_API = `${API_ORIGIN}/api/v1/smiify/sm/settings`;

const DEFAULT_TEMPLATE = {
  id: 'a4-classic-themeClassic',
  name: 'Standard Professional',
  format: 'A4 Document',
  paperClass: 'paperA4',
  themeClass: 'themeClassic',
  layoutClass: 'layoutClassic',
  isThermal: false,
};

const DEFAULT_OPTIONS = {
  showLogo: true,
  showTaxBreakup: true,
  showAmountWords: true,
  showQr: true,
  showTerms: true,
};

/** Share channel name (from the action panel) -> the switch that allows it */
const channelPermission = (channel) => {
  const c = String(channel || '').toLowerCase();
  if (c.includes('api')) return { key: 'billing.send_whatsapp_api', label: 'WhatsApp API' };
  if (c.includes('sms')) return { key: 'billing.send_sms', label: 'SMS' };
  if (c.includes('mail')) return { key: 'billing.send_email', label: 'email' };
  return { key: 'billing.send_whatsapp', label: 'WhatsApp' };       // whatsapp, whatsapp-link, native share
};

const InvoicePreview = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { activeStore } = useStore();
  const { can, canAny, isOwner } = useAccess();
  const token = localStorage.getItem('smiify_token');
  const storeId = activeStore?._id || activeStore?.id;

  // Live access (the server checks the same rules)
  const canDownload = isOwner || can('billing.download_bill');
  const canPrint = isOwner || can('billing.print_bill');
  const canComplete = isOwner || canAny(['billing.complete_bill', 'billing.create']);
  const allowed = useMemo(() => ({
    whatsapp: isOwner || can('billing.send_whatsapp'),
    whatsappApi: isOwner || can('billing.send_whatsapp_api'),
    sms: isOwner || can('billing.send_sms'),
    email: isOwner || can('billing.send_email'),
  }), [isOwner, can]);
  const canShareAny = allowed.whatsapp || allowed.whatsappApi || allowed.sms || allowed.email;

  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const [selectedTemplate, setSelectedTemplate] = useState(DEFAULT_TEMPLATE);
  const [options, setOptions] = useState(DEFAULT_OPTIONS);

  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [shareLink, setShareLink] = useState('');

  const shareLinkRef = useRef('');
  const linkPromiseRef = useRef(null);
  const noticeTimer = useRef(null);

  const viewportRef = useRef(null);
  const docRef = useRef(null);
  const [viewportW, setViewportW] = useState(0);
  const [paperH, setPaperH] = useState(0);
  const [zoom, setZoom] = useState(1);

  const showNotice = (message) => {
    setNotice(message);
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(''), 4500);
  };

  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);

  /* ------------------------------ Data fetch ------------------------------ */
  useEffect(() => {
    let alive = true;
    const fetchData = async () => {
      if (!id || !token || !storeId) return;
      setIsLoadingData(true);
      try {
        const [invRes, setRes] = await Promise.all([
          fetch(`${API_BASE}/${id}`, { headers: { Authorization: `Bearer ${token}`, 'x-store-id': storeId } }),
          fetch(`${SETTINGS_API}?storeId=${encodeURIComponent(storeId)}`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const invData = await invRes.json().catch(() => ({}));
        const setData = await setRes.json().catch(() => ({}));
        if (!alive) return;

        if (invRes.ok && invData.success && invData.data) {
          const found = invData.data.invoice || invData.data;
          setInvoice(found);
          if (found.publicUrl) {
            const link = toAppInvoiceLink(found.publicUrl);   // never a localhost link on the live site
            shareLinkRef.current = link;
            setShareLink(link);
          }
        } else {
          setError(invData.message || 'Invoice not found or access denied.');
        }

        if (setRes.ok && setData.success) setSettings(setData.data || {});
      } catch (err) {
        if (!alive) return;
        console.error('Failed to fetch invoice data:', err);
        setError('Failed to load invoice data.');
      } finally {
        if (alive) setIsLoadingData(false);
      }
    };
    fetchData();
    return () => { alive = false; };
  }, [id, storeId, token]);

  /* ------------------------------ Share link ------------------------------ */
  const getShareLink = () => {
    if (shareLinkRef.current) return Promise.resolve(shareLinkRef.current);
    if (!invoice) return Promise.reject(new Error('Invoice is not loaded yet.'));

    if (!linkPromiseRef.current) {
      linkPromiseRef.current = (async () => {
        const res = await fetch(`${API_BASE}/${invoice._id || invoice.id}/share-link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-store-id': storeId },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.success === false || !data.data?.url) {
          throw new Error(data.message || 'Could not create the invoice link.');
        }
        const link = toAppInvoiceLink(data.data.url);       // never a localhost link on the live site
        shareLinkRef.current = link;
        setShareLink(link);
        return link;
      })().catch((err) => {
        linkPromiseRef.current = null;
        throw err;
      });
    }
    return linkPromiseRef.current;
  };

  // Create the link in the background so WhatsApp opens instantly on click (only if the user may share)
  const invoiceKey = invoice?._id || invoice?.id;
  useEffect(() => {
    if (invoiceKey && canShareAny) getShareLink().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceKey, canShareAny]);

  /* ------------------------ Fit-to-screen preview scale ------------------------ */
  const viewReady = !isLoadingData && Boolean(invoice);

  useEffect(() => {
    if (!viewReady || !viewportRef.current) return undefined;
    const ro = new ResizeObserver(([entry]) => setViewportW(entry.contentRect.width));
    ro.observe(viewportRef.current);
    return () => ro.disconnect();
  }, [viewReady]);

  useEffect(() => {
    if (!viewReady || !docRef.current) return undefined;
    const el = docRef.current;
    setPaperH(el.offsetHeight);
    const ro = new ResizeObserver(() => setPaperH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [viewReady, selectedTemplate]);

  /* ------------------------------ Derived values ------------------------------ */
  const brand = getInvoiceBrand(settings || {}, invoice || {}, activeStore);
  const invoiceNo = invoice?.invoiceNo || invoice?.invoiceMeta?.invoiceNo || 'DRAFT';
  const fileBase = sanitizeFileName(`${brand.salonName}-${invoiceNo}`);
  const getPaperNode = () => docRef.current?.firstElementChild || null;

  const customerName = cleanCustomerName(invoice?.customer?.name) || 'Walk-in customer';
  const customerPhone = String(invoice?.customer?.phone || '').replace(/\D/g, '').slice(-10);
  const waPhone = customerPhone.length === 10 ? normalizePhone(customerPhone) : '';

  const paperPx = getPaperWidthPx(selectedTemplate);
  const fitScale = viewportW ? Math.min(selectedTemplate.isThermal ? 1.4 : 1, (viewportW - 40) / paperPx) : 1;
  const scale = Math.max(0.3, fitScale * zoom);

  const itemCount = (invoice?.items || []).reduce((s, it) => s + (Number(it.qty) || 1), 0);
  const buildMessage = (channel, link) =>
    buildShareMessage({
      channel,
      customerName,
      salonName: brand.salonName,
      salonPhone: brand.phone,
      invoiceNo,
      date: invoice ? formatDate(invoice.date) : '',
      total: invoice?.summary?.grandTotal,
      itemCount,
      link,
    });

  /* ------------------------------ Actions ------------------------------ */
  const handleComplete = async () => {
    if (!invoice || invoice.status !== 'draft') return;
    if (!canComplete) { setError('You do not have access to complete bills.'); return; }
    setActionLoading('complete');
    setError('');
    try {
      const response = await fetch(`${API_BASE}/${invoice._id || invoice.id}/complete`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-store-id': storeId },
        body: JSON.stringify(invoice),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setInvoice((current) => ({ ...current, ...(data.data || {}), status: 'completed' }));
        showNotice('Bill completed.');
      } else {
        setError(data.message || 'Failed to complete invoice.');
      }
    } catch (err) {
      console.error(err);
      setError('Network error. Failed to complete bill.');
    } finally {
      setActionLoading('');
    }
  };

  const handlePrint = async () => {
    if (!canPrint) { setError('You do not have access to print bills.'); return; }
    setError('');
    setActionLoading('print');
    try {
      await printInvoiceNode(getPaperNode(), selectedTemplate, fileBase);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not open the print dialog.');
    } finally {
      setActionLoading('');
    }
  };

  const handleDownload = async () => {
    if (!canDownload) { setError('You do not have access to download bills.'); return; }
    setError('');
    setActionLoading('pdf');
    try {
      const blob = await buildInvoicePdf(getPaperNode(), selectedTemplate);
      downloadBlob(blob, `${fileBase}.pdf`);
      showNotice('PDF downloaded.');
    } catch (err) {
      console.error(err);
      setError('Could not create the PDF. Please try again.');
    } finally {
      setActionLoading('');
    }
  };

  // WhatsApp Cloud API: backend sends the PDF + message to the customer
  const sendViaBackend = async (payload) => {
    const res = await fetch(`${API_BASE}/${invoice._id || invoice.id}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-store-id': storeId },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 413) throw new Error('PDF is too large for the server. Increase the express.json limit.');
    if (!res.ok || data.success === false) throw new Error(data.message || 'WhatsApp API could not send the invoice.');
    return data;
  };

  const handleShare = async (channel) => {
    if (!invoice) return;
    const perm = channelPermission(channel);
    if (!isOwner && !can(perm.key)) {          // hard stop, even if a button is still on screen
      setError(`You do not have access to share bills by ${perm.label}.`);
      return;
    }
    setError('');
    setActionLoading(channel);
    try {
      const result = await shareInvoice({
        channel,
        recipient: waPhone,
        email: invoice.customer?.email || '',
        node: getPaperNode(),
        template: selectedTemplate,
        fileName: fileBase,
        subject: `Your invoice ${invoiceNo} from ${brand.salonName}`,
        getLink: getShareLink,
        buildMessage,
        apiSender: sendViaBackend,
      });

      const messages = {
        'whatsapp-link': waPhone
          ? `WhatsApp opened for ${customerName}. Tap send.`
          : 'WhatsApp opened. Choose the contact and tap send.',
        native: 'Share sheet opened. Choose WhatsApp to send the PDF.',
        'whatsapp-web': 'Link unavailable, so the PDF was downloaded. Attach it in WhatsApp and send.',
        api: `Invoice sent to ${customerName} on WhatsApp.`,
        sms: 'SMS app opened with the message.',
        'gmail-link': 'Gmail opened with the message and invoice link.',
        gmail: 'PDF downloaded and Gmail opened. Attach the PDF and send.',
      };
      if (result.mode !== 'cancelled') showNotice(messages[result.mode] || 'Done.');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not share the invoice.');
    } finally {
      setActionLoading('');
    }
  };

  /* ------------------------------ Render ------------------------------ */
  if (isLoadingData) {
    return (
      <div className={`${styles.previewPage} ${styles.centered}`}>
        <Loader2 className={styles.spin} size={30} color="var(--h-color-primary-600, #673de6)" />
        <p className={styles.centerText}>Loading invoice...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className={`${styles.previewPage} ${styles.centered}`}>
        <h3 className={styles.centerTitle}>{error || 'Invoice not found'}</h3>
        <button type="button" onClick={() => navigate('/create-bill')} className={styles.goBack}>
          Back to billing
        </button>
      </div>
    );
  }

  const isDraft = invoice.status === 'draft';
  const billPath = `/create-bill/${invoice._id || invoice.id}`;

  return (
    <div className={styles.previewPage}>
      <header className={styles.previewTopBar}>
        <div className={styles.topBarLeft}>
          <button type="button" className={styles.backButton} onClick={() => navigate(isDraft ? billPath : '/create-bill')} aria-label="Back to billing">
            <ArrowLeft size={15} />
          </button>
          <div className={styles.topTitle}>
            <span>Invoice preview</span>
            <strong>{invoiceNo}</strong>
          </div>
          <span className={`${styles.statusPill} ${isDraft ? styles.statusDraft : styles.statusCompleted}`}>
            {isDraft ? <FileText size={11} /> : <CheckCircle2 size={11} />}
            {isDraft ? 'Draft' : 'Completed'}
          </span>
        </div>

        <div className={styles.topBarCenter}>
          <Palette size={13} />
          <span>{selectedTemplate.name}</span>
          <i />
          <span>{selectedTemplate.format}</span>
        </div>

        <div className={styles.topBarRight}>
          <div className={styles.zoomGroup} role="group" aria-label="Zoom">
            <button type="button" onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.1).toFixed(1)))} aria-label="Zoom out">
              <ZoomOut size={13} />
            </button>
            <button type="button" className={styles.zoomValue} onClick={() => setZoom(1)} title="Reset zoom">
              {Math.round(scale * 100)}%
            </button>
            <button type="button" onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(1)))} aria-label="Zoom in">
              <ZoomIn size={13} />
            </button>
          </div>

          {canDownload && (
            <button type="button" className={styles.topActionGhost} onClick={handleDownload} disabled={Boolean(actionLoading)}>
              {actionLoading === 'pdf' ? <Loader2 size={13} className={styles.spin} /> : <Download size={13} />} PDF
            </button>
          )}
          {canPrint && (
            <button type="button" className={styles.topAction} onClick={handlePrint} disabled={Boolean(actionLoading)}>
              {actionLoading === 'print' ? <Loader2 size={13} className={styles.spin} /> : <Printer size={13} />} Print
            </button>
          )}
        </div>
      </header>

      <div className={styles.previewWorkspace}>
        <InvoiceTemplatePanel
          selectedTemplate={selectedTemplate}
          onSelectTemplate={setSelectedTemplate}
          options={options}
          onOptionsChange={setOptions}
        />

        <main className={styles.invoiceViewport} ref={viewportRef} aria-label="Invoice preview">
          <div className={styles.paperFrame} style={{ width: paperPx * scale, height: paperH ? paperH * scale : 'auto' }}>
            <div ref={docRef} className={styles.paperScaler} style={{ width: paperPx, transform: `scale(${scale})` }}>
              <InvoiceDocument
                invoice={invoice}
                settings={settings || {}}
                store={activeStore}
                template={selectedTemplate}
                options={options}
              />
            </div>
          </div>
        </main>

        <InvoiceActionPanel
          invoice={invoice}
          hasPhone={customerPhone.length === 10}
          messagePreview={buildMessage('whatsapp', shareLink)}
          shareLink={shareLink}
          actionLoading={actionLoading}
          error={error}
          notice={notice}
          onShare={handleShare}
          onComplete={handleComplete}
          allowed={allowed}
          canShare={canShareAny}
          canComplete={canComplete}
        />
      </div>
    </div>
  );
};

export default InvoicePreview;